import { createFileRoute } from '@tanstack/react-router'

import { EntryScreen } from '@/components/entry'
import { ONBOARDING_STEPS } from '@/components/onboarding/steps'
import { AppButton, SectionLabel } from '@/components/shared'
import { TEAM } from '@/mocks/onboarding/vitejte'

export const Route = createFileRoute('/onboarding/vitejte')({
  head: () => ({ meta: [{ title: 'Vítejte · Aplikace Bitez' }] }),
  component: OnboardingVitejte,
})

/**
 * The wizard's cover — the first screen inside the product.
 *
 * It keeps the entry shell (same room, one sheet deeper) rather than switching
 * to the working canvas: the client just typed a password, and landing on a
 * plain form would read as "nothing happened". The steps themselves change gear.
 *
 * Three jobs, in this order: **who** will look after you (the faces are the
 * emotional payload and they are real people, not roles), **what** is about to
 * happen (the itinerary — three numbered lines, so nobody is asked to trust an
 * open-ended form), and the **promise** that it is short and interruptible.
 * That promise is the reason this screen exists at all: a resumable wizard has
 * to say so, or people don't leave it, they abandon it.
 *
 * It deliberately does **not** ask them to confirm their own name and phone
 * (owner, 2026-08-20). They arrived from an e-mail addressed to them and set a
 * password thirty seconds ago — identity is the one question they have already
 * answered, and asking it again is the app admiring its own data. The details
 * live in Nastavení → Váš účet, where a correction belongs.
 *
 * "Tři otázky, tři minuty" is a promise the wizard can now actually keep
 * (owner, 2026-08-20): everything that needed a file, a password or a colleague
 * left for the second run, so nothing in here can stall on someone else.
 */
function OnboardingVitejte() {
  const names = TEAM.map((member) => member.name).join(' a ')
  const roles = TEAM.map((member) => member.note).join(' a ')

  return (
    <EntryScreen sticker="home">
      <SectionLabel>Vítejte</SectionLabel>
      <h1 className="font-display mt-2 text-3xl leading-[0.88] uppercase">Pojďme na to</h1>
      <p className="mt-3 text-sm leading-relaxed text-ink/60">
        Tři otázky, tři minuty. Nic k tomu nepotřebujete — všechno víte z hlavy.
      </p>

      <div className="mt-6 flex items-center gap-3">
        <span aria-hidden className="flex shrink-0 -space-x-3">
          {TEAM.map((member) => (
            <img
              key={member.name}
              src={member.avatar}
              alt=""
              width={150}
              height={150}
              className="size-11 rounded-full object-cover ring-2 ring-cream"
            />
          ))}
        </span>
        <span className="min-w-0">
          <span className="block text-sm font-bold">{names} už na vás čekají</span>
          <span className="block truncate text-[10px] font-semibold text-ink/45">{roles}</span>
        </span>
      </div>

      <div aria-hidden className="my-6 border-t-2 border-dashed border-ink/10" />

      {/* The itinerary — the same numbered ink circles the photographer's rewards
          sheet uses, so a numbered list means the same thing across the app. */}
      <ol className="space-y-3.5">
        {ONBOARDING_STEPS.map((step, index) => (
          <li key={step.title} className="flex items-center gap-3">
            <span
              aria-hidden
              className="initial font-display flex size-6 shrink-0 items-center justify-center rounded-full bg-ink text-[11px] text-cream"
            >
              {index + 1}
            </span>
            <span className="text-sm font-semibold text-ink/75">{step.title}</span>
          </li>
        ))}
      </ol>

      <AppButton size="lg" href="/onboarding/podnik" className="mt-7 w-full">
        Začít
      </AppButton>

      <p className="mt-4 text-center text-[10px] leading-relaxed font-semibold text-ink/40">
        Můžete kdykoliv přestat. Co vyplníte, zůstane uložené.
      </p>
    </EntryScreen>
  )
}
